import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HelpCircle, ChevronDown, Lightbulb, Info, BookOpen } from "lucide-react";
import { cn } from "@/lib/utils";

// Expandable help card component
export const HelpCard = ({ 
  title, 
  children, 
  icon: Icon = Lightbulb, 
  defaultOpen = false,
  variant = "default" // "default", "tip", "info", "important"
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const variants = {
    default: "bg-muted/50 border-border",
    tip: "bg-accent/30 border-accent",
    info: "bg-blue-50 border-blue-200",
    important: "bg-primary/5 border-primary/20"
  };

  const iconColors = {
    default: "text-muted-foreground",
    tip: "text-accent-foreground",
    info: "text-blue-600",
    important: "text-primary"
  };

  return (
    <div className={cn(
      "rounded-lg border overflow-hidden transition-all duration-200",
      variants[variant]
    )}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 text-left hover:bg-foreground/5 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className={cn("w-4 h-4", iconColors[variant])} />
          <span className="text-sm font-medium text-foreground">{title}</span>
        </div>
        <ChevronDown 
          className={cn(
            "w-4 h-4 text-muted-foreground transition-transform duration-200",
            isOpen && "rotate-180"
          )} 
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 pt-0 text-sm text-muted-foreground leading-relaxed">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Simple inline help text
export const HelpText = ({ children, icon: Icon = Info }) => (
  <div className="flex items-start gap-2 text-sm text-muted-foreground bg-muted/30 rounded-lg p-3">
    <Icon className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
    <span className="leading-relaxed">{children}</span>
  </div>
);

// Context explanation banner
export const ContextBanner = ({ title, description, icon: Icon = BookOpen }) => (
  <motion.div
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6"
  >
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <div>
        <h3 className="font-semibold text-foreground text-sm mb-1">{title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
      </div>
    </div>
  </motion.div>
);

// Why this matters explanation
export const WhyItMatters = ({ children }) => (
  <div className="border-l-2 border-primary/30 pl-4 py-1 my-3">
    <p className="text-xs font-medium text-primary uppercase tracking-wide mb-1">Why this matters</p>
    <p className="text-sm text-muted-foreground leading-relaxed">{children}</p>
  </div>
);

// Example prompt
export const ExamplePrompt = ({ label, examples }) => (
  <div className="bg-secondary/50 rounded-lg p-3 mt-3">
    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">{label}</p>
    <ul className="space-y-1">
      {examples.map((example, i) => (
        <li key={i} className="text-sm text-foreground flex items-start gap-2">
          <span className="text-primary">•</span>
          <span>{example}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default HelpCard;
